// pages/master/menu/PackageMenuAddEdit.jsx
'use client';

import { useState, useEffect } from 'react';
import { Box, TextField, Button } from '@mui/material';

import FormDialog from '../../../components/FormDialog';
import { createPackageMenu, updatePackageMenu } from '../../../stores/roleStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';
import { useNotifier } from '../../../components/Notifier';

const PackageMenuAddEdit = ({ open, onClose, isEdit = false, initialData = null, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();
  const [packageName, setPackageName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setPackageName(initialData.package_name || '');
    } else {
      setPackageName('');
    }
  }, [open, initialData]);

  const handleSave = async () => {
    if (!packageName.trim()) return;
    setSaving(true);

    try {
      const payload = { package_name: formatUpperCase(packageName) };

      if (isEdit && initialData?.id) {
        // Jika endpoint PUT butuh ID di dalam payload, kita sertakan di sini
        payload.id = initialData.id;
        await updatePackageMenu(payload);
        notify({ message: t('packageMenu.packageUpdatedSuccess', 'Package successfully updated'), severity: 'success' });
      } else {
        await createPackageMenu(payload);
        notify({ message: t('packageMenu.packageCreatedSuccess', 'New package successfully created'), severity: 'success' });
      }

      if (typeof onSuccess === 'function') onSuccess();
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage =
        err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('packageMenu.editPackageMenu', 'Edit Package Menu') : t('packageMenu.createNewPackage', 'Create New Package')}
      maxWidth="xs"
      actions={
        <>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !packageName.trim()}
            disableElevation
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: '#0072DA',
              '&:hover': { backgroundColor: '#005bb5' }
            }}
          >
            {saving ? t('general.saving', 'Saving...') : t('general.save', 'Save')}
          </Button>
          <Button onClick={onClose} variant="outlined" disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {t('general.cancel', 'Cancel')}
          </Button>
        </>
      }
    >
      <Box sx={{ p: 1, pt: 2, pb: 2 }}>
        <TextField
          autoFocus
          fullWidth
          label={t('packageMenu.packageName', 'Package Name')}
          variant="outlined"
          value={packageName}
          onChange={(e) => setPackageName(formatUpperCase(e.target.value))}
          placeholder={t('packageMenu.egTrackingMonitoring', 'e.g. Tracking Monitoring')}
          disabled={saving}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>
    </FormDialog>
  );
};

export default PackageMenuAddEdit;
